import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import KPICard from '../analysis-results/components/KPICard';
import {
  GP3R_STORAGE_KEY,
  demoNewProperty,
  demoPortfolio,
  demoProfiles,
  guidedSteps,
  portfolioSummary,
  profileResults,
} from './gp3rDemoData';

const formatCurrency = (value) => new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
}).format(value || 0);

const toneClasses = {
  balanced: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
    border: 'rgba(245, 158, 11, 0.3)',
    icon: 'Minus',
    title: 'À considérer',
  },
  good: {
    color: '#22c55e',
    bg: 'rgba(34, 197, 94, 0.08)',
    border: 'rgba(34, 197, 94, 0.2)',
    icon: 'ThumbsUp',
    title: 'Bon investissement',
  },
  strong: {
    color: '#16a34a',
    bg: 'rgba(34, 197, 94, 0.1)',
    border: 'rgba(34, 197, 94, 0.3)',
    icon: 'ThumbsUp',
    title: 'Excellent potentiel',
  },
};

const Gp3rDemo = () => {
  const navigate = useNavigate();
  const [profileId, setProfileId] = useState('');
  const [step, setStep] = useState('profile');

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(GP3R_STORAGE_KEY);
      if (stored && demoProfiles[stored]) {
        setProfileId(stored);
        setStep('dashboard');
      }
    } catch {
      // Session storage is optional for this public demo.
    }
  }, []);

  const profile = profileId ? demoProfiles[profileId] : null;
  const result = profileId ? profileResults[profileId] : null;

  const progressIndex = useMemo(() => {
    const order = ['profile', 'dashboard', 'guided', 'results'];
    return order.indexOf(step) + 1;
  }, [step]);

  const selectProfile = (id) => {
    setProfileId(id);
    try {
      sessionStorage.setItem(GP3R_STORAGE_KEY, id);
    } catch {
      // No-op: the demo can still continue without persistence.
    }
    setStep('dashboard');
  };

  const resetDemo = () => {
    setProfileId('');
    setStep('profile');
    try {
      sessionStorage.removeItem(GP3R_STORAGE_KEY);
    } catch {
      // No-op.
    }
  };

  const Progress = () => (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {[1, 2, 3, 4].map((item) => (
        <div
          key={item}
          className={`h-2 rounded-full transition-all ${item <= progressIndex ? 'bg-primary w-8' : 'bg-muted w-4'}`}
        />
      ))}
    </div>
  );

  const Header = () => (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <button type="button" onClick={() => navigate('/')} className="flex items-center gap-2">
          <img src="/immoiq-logo.png" alt="ImmoIQ" className="h-8 w-auto" />
          <span className="font-bold text-foreground">ImmoIQ</span>
          <span className="hidden sm:inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-semibold">
            Démo GP3R
          </span>
        </button>
        <div className="flex items-center gap-3">
          <Progress />
          {profile && (
            <button type="button" onClick={resetDemo} className="text-xs text-muted-foreground hover:text-foreground">
              Recommencer
            </button>
          )}
        </div>
      </div>
    </header>
  );

  const ProfileStep = () => (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 text-primary px-3 py-1 text-sm font-medium mb-4">
          <Icon name="Flag" size={16} />
          Événement GP3R
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
          Découvrez ImmoIQ avec un compte démo
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Choisissez votre niveau d’aisance en investissement immobilier. La démo adaptera le dashboard,
          l’analyse guidée et les résultats à votre profil.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {Object.values(demoProfiles).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => selectProfile(item.id)}
            className="rounded-3xl border border-border bg-card p-6 text-left hover:border-primary hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid={`gp3r-profile-${item.id}`}
          >
            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
              <Icon name={item.icon} size={24} />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">{item.label}</h2>
            <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
            <p className="text-sm font-medium text-foreground">{item.headline}</p>
          </button>
        ))}
      </div>
    </section>
  );

  const DashboardStep = () => (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-sm font-semibold text-primary mb-2">Compte démo {profile.shortLabel}</p>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
            Portefeuille déjà rempli
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">{profile.guidanceTone}</p>
        </div>
        <Button iconName="Sparkles" iconPosition="left" onClick={() => setStep('guided')} data-testid="gp3r-start-guided-analysis">
          Analyser un nouvel immeuble
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard icon="Building2" label="Valeur totale" value={formatCurrency(portfolioSummary.totalValue)} />
        <SummaryCard icon="Wallet" label="Équité nette" value={formatCurrency(portfolioSummary.totalEquity)} />
        <SummaryCard icon="TrendingUp" label="Cashflow mensuel" value={formatCurrency(portfolioSummary.monthlyCashflow)} tone="good" />
        <SummaryCard icon="Gauge" label="Score moyen" value={`${portfolioSummary.averageScore}/100`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {demoPortfolio.map((property) => (
          <div key={property.id} className="rounded-3xl border border-border bg-card overflow-hidden">
            <div className="h-36 bg-gradient-to-br from-primary/20 via-muted to-emerald-500/10 flex items-center justify-center">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="font-bold text-foreground">{property.address}</h2>
                  <p className="text-sm text-muted-foreground">{property.city} • {property.type} • {property.units} unités</p>
                </div>
                <span className="rounded-xl bg-primary/10 text-primary px-3 py-1 text-sm font-bold">{property.score}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <MiniMetric label="Valeur" value={formatCurrency(property.value)} />
                <MiniMetric label="Cashflow" value={formatCurrency(property.monthlyCashflow)} positive />
              </div>
              <p className="mt-4 text-sm text-muted-foreground">{property.note}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const GuidedStep = () => (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <button type="button" onClick={() => setStep('dashboard')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <Icon name="ArrowLeft" size={16} />
        Retour au dashboard démo
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
          <p className="text-sm font-semibold text-primary mb-2">Analyse guidée</p>
          <h1 className="text-3xl font-bold text-foreground mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Aucun formulaire à remplir
          </h1>
          <p className="text-muted-foreground mb-8">
            Pour la démo GP3R, ImmoIQ charge un immeuble fictif réaliste et simule l’analyse complète.
          </p>

          <div className="space-y-4">
            {guidedSteps.map((item, index) => (
              <div key={item.id} className="flex gap-4 rounded-2xl border border-border bg-background p-4">
                <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon name={item.icon} size={21} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Étape {index + 1}</div>
                  <h2 className="font-bold text-foreground">{item.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button iconName="Play" iconPosition="left" onClick={() => setStep('results')} data-testid="gp3r-view-results">
              Lancer l’analyse et voir les résultats
            </Button>
            <Button variant="outline" onClick={() => setStep('dashboard')}>
              Voir le portefeuille
            </Button>
          </div>
        </div>

        <PropertyPreview />
      </div>
    </section>
  );

  const ResultsStep = () => (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <button type="button" onClick={() => setStep('guided')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <Icon name="ArrowLeft" size={16} />
        Retour à l’analyse guidée
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          <ResultsHero profile={profile} result={result} />

          <div className={`grid grid-cols-1 ${result.kpis.length > 3 ? 'md:grid-cols-2 xl:grid-cols-3' : 'md:grid-cols-3'} gap-4`}>
            {result.kpis.map((kpi) => (
              <DemoKpiCard key={kpi.label} kpi={kpi} />
            ))}
          </div>

          {result.projection && (
            <ProjectionPreview
              title={result.projectionTitle}
              data={result.projection}
              advanced={profileId === 'advanced'}
            />
          )}

          {result.returnBreakdown && (
            <ReturnBreakdown items={result.returnBreakdown} />
          )}

          <div className="rounded-xl border border-border bg-card p-5 md:p-6">
            <h2 className="text-xl font-bold text-foreground mb-3">Lecture ImmoIQ</h2>
            <p className="text-muted-foreground mb-5">{result.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InsightList title="Ce qu’il faut retenir" icon="Lightbulb" items={result.insights} />
              <InsightList title="Prochaines actions" icon="ListChecks" items={result.nextActions} />
            </div>
          </div>

          {result.planningPreview && (
            <PlanningPreview items={result.planningPreview} />
          )}
        </div>

        <aside className="space-y-5">
          <PropertyPreview compact />
          <div className="rounded-3xl border border-primary/25 bg-primary/5 p-6">
            <Icon name="Gift" size={24} className="text-primary mb-3" />
            <h2 className="font-bold text-foreground mb-2">Essayez avec vos vrais immeubles</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Créez un compte gratuit et utilisez vos 3 analyses pour tester ImmoIQ avec vos propres opportunités.
            </p>
            <div className="space-y-3">
              <Button fullWidth onClick={() => navigate('/register')} data-testid="gp3r-create-account">
                Créer mon compte gratuit
              </Button>
              <Button fullWidth variant="outline" onClick={() => navigate('/subscription-pricing')}>
                Voir les plans
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {step === 'profile' && <ProfileStep />}
      {step === 'dashboard' && profile && <DashboardStep />}
      {step === 'guided' && profile && <GuidedStep />}
      {step === 'results' && profile && result && <ResultsStep />}
    </div>
  );
};

const ResultsHero = ({ profile, result }) => {
  const tone = toneClasses[result.tone] || toneClasses.balanced;

  return (
    <>
      <div className="bg-card border border-border rounded-xl p-4 md:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground truncate">
                {demoNewProperty.address}
              </h1>
              <span className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground text-sm font-medium w-fit">
                Analyse démo
              </span>
              <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-sm font-medium w-fit">
                Profil {profile.shortLabel}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 md:gap-4 caption text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>{demoNewProperty.city}, QC</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Home" size={16} />
                <span>{demoNewProperty.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="DoorOpen" size={16} />
                <span>{demoNewProperty.units} unités</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} />
                <span>Analyse GP3R</span>
              </div>
            </div>
          </div>

          <div className="hidden lg:flex w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-muted items-center justify-center">
            <Icon name="Building2" size={34} className="text-primary" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 mt-4 border-t border-border/50">
          <HeaderMetric label="Prix demandé" value={formatCurrency(demoNewProperty.askingPrice)} />
          <HeaderMetric label="Revenus mensuels" value={formatCurrency(demoNewProperty.monthlyRevenue)} />
          <HeaderMetric label="Taux" value={`${demoNewProperty.interestRate} %`} />
          <HeaderMetric label="Mise de fonds" value={formatCurrency(demoNewProperty.downPayment)} />
        </div>
      </div>

      <div
        className="rounded-xl p-5 md:p-6"
        style={{
          background: tone.bg,
          border: `2px solid ${tone.border}`,
        }}
      >
        <div className="flex items-start gap-5">
          <div
            className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${tone.color}20` }}
          >
            <Icon name={tone.icon} size={28} color={tone.color} />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: tone.color }}>
                {tone.title}
              </h2>
              <span
                className="text-sm font-bold px-3 py-1 rounded-full"
                style={{ color: tone.color, background: `${tone.color}18` }}
              >
                Score {result.score}
              </span>
            </div>
            <p className="text-muted-foreground mt-2 leading-relaxed">{result.summary}</p>
            <p className="text-sm font-semibold mt-3" style={{ color: tone.color }}>
              Verdict : {result.verdict}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const DemoKpiCard = ({ kpi }) => (
  <KPICard
    title={kpi.label}
    value={kpi.value}
    subtitle={kpi.helper}
    icon={kpi.icon || 'BarChart3'}
  />
);

const ProjectionPreview = ({ title, data, advanced = false }) => {
  const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="caption font-medium text-foreground mb-2">Année {label}</p>
          {payload.map((entry) => (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4 mb-1">
              <span className="caption" style={{ color: entry.color }}>
                {entry.name}:
              </span>
              <span className="caption font-medium data-text" style={{ color: entry.color }}>
                {entry.dataKey === 'annualROI' ? formatPercent(entry.value) : formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold text-foreground">{title || 'Projection sur la durée du prêt'}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Évolution de la valeur, de l’hypothèque et du ROI annualisé sur {demoNewProperty.amortization} ans.
          </p>
        </div>
        <span className="rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">
          Durée du prêt
        </span>
      </div>

      <div className="w-full h-64 md:h-80 lg:h-96" aria-label="Graphique combiné GP3R">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis
              dataKey="year"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '0.75rem', fontFamily: 'var(--font-caption)' }}
              label={{ value: 'Années', position: 'insideBottom', offset: -5, style: { fontSize: '0.75rem', fontFamily: 'var(--font-caption)' } }}
            />
            <YAxis
              yAxisId="left"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '0.75rem', fontFamily: 'var(--font-caption)' }}
              tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '0.75rem', fontFamily: 'var(--font-caption)' }}
              tickFormatter={formatPercent}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                fontSize: '0.875rem',
                fontFamily: 'var(--font-caption)',
                paddingTop: '1rem',
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="propertyValue"
              name="Valeur de la propriété"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="mortgageBalance"
              name="Hypothèque"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ fill: '#ef4444', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="annualROI"
              name="ROI annualisé"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ fill: '#22c55e', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className={`grid grid-cols-2 ${advanced ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-3 mt-5`}>
        <MiniMetric label="Équité année 1" value={formatCurrency(data[0]?.equity)} />
        <MiniMetric label={`Équité année ${data[data.length - 1]?.year}`} value={formatCurrency(data[data.length - 1]?.equity)} positive />
        {advanced && (
          <MiniMetric label="Retour cumulé final" value={formatCurrency(data[data.length - 1]?.cumulativeReturn)} positive />
        )}
      </div>
    </div>
  );
};

const ReturnBreakdown = ({ items }) => (
  <div className="rounded-xl border border-border bg-card p-5 md:p-6">
    <h2 className="text-xl font-bold text-foreground mb-2">Détail du rendement total</h2>
    <p className="text-sm text-muted-foreground mb-5">
      Répartition approximative du rendement généré sur la durée du prêt.
    </p>
    <div className="space-y-4">
      {items.map((item) => (
        <div key={item.label}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-medium text-foreground">{item.label}</span>
            <span className="text-sm text-muted-foreground">{formatCurrency(item.value)} • {item.percent} %</span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${item.percent}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PlanningPreview = ({ items }) => (
  <div className="rounded-xl border border-primary/25 bg-primary/5 p-5 md:p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon name="Route" size={22} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-foreground">Avant-goût d’une planification</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Le mode Pro permettrait de transformer cette analyse en stratégie sur plusieurs années.
        </p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {items.map((item) => (
        <div key={item.year} className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold text-primary">{item.year}</p>
          <h3 className="font-bold text-foreground mt-1">{item.title}</h3>
          <p className="text-sm text-muted-foreground mt-2">{item.detail}</p>
        </div>
      ))}
    </div>
  </div>
);

const SummaryCard = ({ icon, label, value, tone = 'neutral' }) => (
  <div className="rounded-3xl border border-border bg-card p-5">
    <div className="flex items-center gap-3 mb-4">
      <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${tone === 'good' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-primary/10 text-primary'}`}>
        <Icon name={icon} size={20} />
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
    <p className="text-2xl font-bold text-foreground">{value}</p>
  </div>
);

const HeaderMetric = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className="text-sm md:text-base font-bold text-foreground">{value}</p>
  </div>
);

const MiniMetric = ({ label, value, positive = false }) => (
  <div className="rounded-2xl bg-muted/40 p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm font-bold mt-1 ${positive ? 'text-emerald-600' : 'text-foreground'}`}>{value}</p>
  </div>
);

const PropertyPreview = ({ compact = false }) => (
  <div className="rounded-3xl border border-border bg-card overflow-hidden">
    <div className={`${compact ? 'h-28' : 'h-40'} bg-gradient-to-br from-primary/20 via-muted to-amber-500/10 flex items-center justify-center`}>
      <Icon name="Building2" size={compact ? 34 : 46} className="text-primary" />
    </div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-bold text-foreground">{demoNewProperty.address}</h2>
          <p className="text-sm text-muted-foreground">
            {demoNewProperty.city} • {demoNewProperty.type} • {demoNewProperty.units} unités
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <MiniMetric label="Prix demandé" value={formatCurrency(demoNewProperty.askingPrice)} />
        <MiniMetric label="Revenus" value={formatCurrency(demoNewProperty.monthlyRevenue)} positive />
        <MiniMetric label="Taux" value={`${demoNewProperty.interestRate} %`} />
        <MiniMetric label="Mise de fonds" value={formatCurrency(demoNewProperty.downPayment)} />
      </div>
      {!compact && (
        <p className="text-sm text-muted-foreground mt-4">{demoNewProperty.neighborhood}</p>
      )}
    </div>
  </div>
);

const InsightList = ({ title, icon, items }) => (
  <div className="rounded-2xl border border-border bg-background p-4">
    <h3 className="font-bold text-foreground flex items-center gap-2 mb-3">
      <Icon name={icon} size={18} className="text-primary" />
      {title}
    </h3>
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex gap-2 text-sm text-muted-foreground">
          <Icon name="Check" size={16} className="text-emerald-600 shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default Gp3rDemo;
